/**
 * Computes the highlight set for a selected cluster: the cluster's own member
 * nodes, plus every node they connect to (inside or outside the cluster), plus
 * the edges touching a member. This mirrors the node-click behavior (a node +
 * its direct neighbours) at the cluster granularity and feeds the same
 * HighlightContext, so nodes/edges dim and accent exactly as they already do.
 *
 * Each graph view normalises its own edge shape (build: connection_id /
 * from_node_id / to_node_id; research: `${source}-${target}` keys) to
 * HighlightEdge before calling this, keeping the helper view-agnostic.
 */

import { EMPTY_HIGHLIGHT } from './HighlightContext';
import type { HighlightState } from './HighlightContext';

export interface HighlightEdge {
  source: string;
  target: string;
  /** React Flow edge id, matched against HighlightState.edgeKeys by edge components. */
  key: string;
}

export function clusterHighlight(memberIds: string[], edges: HighlightEdge[]): HighlightState {
  if (memberIds.length === 0) {
    return EMPTY_HIGHLIGHT;
  }

  const members = new Set(memberIds);
  const nodeIds = new Set<string>(memberIds);
  const edgeKeys = new Set<string>();

  edges.forEach((edge) => {
    if (members.has(edge.source) || members.has(edge.target)) {
      nodeIds.add(edge.source);
      nodeIds.add(edge.target);
      edgeKeys.add(edge.key);
    }
  });

  // mode 'cluster': the header is the focus, so edge components ghost the part
  // of a highlighted arrow that crosses a cluster header to 30% over it.
  return { nodeIds, edgeKeys, active: true, mode: 'cluster' };
}
